import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { ApiFetchFn } from '../api';
import type { AdminInvoiceAutopayChargeResult, AdminInvoiceAutopayPreview } from '../types/models/Wallet';
import type {
  ApiResponseAdminInvoiceAutopayCharge,
  ApiResponseAdminInvoiceAutopayPreview,
} from '../types/api/ApiResponseAdminWallet';

// Staff-only reads and charges over docs/api-types/admin-invoice-autopay.md: staff collect an
// organization's unpaid invoices on the organization's OWN consent (its invoice autopay opt-in), recorded
// as the staff user's action. Mount behind `is_ancalagon_staff` so a customer never fires them. JSend at
// HTTP 200 — branch on `body.status`; a plain HTTP 404 means the backend does not serve the route yet.

export interface UseAdminInvoiceAutopayReturn {
  /** The consent, balance, card and every unpaid invoice with its plan; `null` until loaded / on error. */
  preview: AdminInvoiceAutopayPreview | null;
  /** `false` once the backend answered HTTP 404 — the route isn't deployed; the consumer hides the feature. */
  available: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * `GET /admin/invoice-autopay/{clientId}` — what a charge would do today for each of the organization's
 * unpaid invoices (oldest first). Fetches only while `enabled` and `clientId` is set; a newer request
 * aborts the previous one.
 */
export const useAdminInvoiceAutopay = (clientId: number | null, enabled: boolean): UseAdminInvoiceAutopayReturn => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [preview, setPreview] = useState<AdminInvoiceAutopayPreview | null>(null);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState<boolean>(enabled && clientId !== null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Another organization: never show the previous one's invoices under it.
  useEffect(() => {
    setPreview(null);
    setAvailable(true);
    setError(null);
  }, [clientId]);

  const fetchPreview = useCallback(async () => {
    if (clientId === null) {
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/admin/invoice-autopay/${clientId}`, { signal: controller.signal });
      if (response.status === 404) {
        setAvailable(false);
        setPreview(null);
        return;
      }
      const body = (await response.json().catch(() => null)) as ApiResponseAdminInvoiceAutopayPreview | null;
      if (body?.status === 'success' && body.data?.invoice_autopay) {
        setAvailable(true);
        setPreview(body.data);
        return;
      }
      setError(body?.message ?? "Could not load the organization's unpaid invoices.");
    } catch (err) {
      if ((err instanceof DOMException && err.name === 'AbortError') || controller.signal.aborted) {
        return;
      }
      setError(
        err instanceof Error ? err.message : "An error occurred while loading the organization's unpaid invoices.",
      );
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [apiFetch, clientId]);

  useEffect(() => {
    if (enabled && clientId !== null && auth.isAuthenticated) {
      void fetchPreview();
    }
    return () => abortRef.current?.abort();
  }, [enabled, clientId, auth.isAuthenticated, fetchPreview]);

  return { preview, available, loading, error, refetch: fetchPreview };
};

/** What one charge request came back with. */
export type ChargeInvoiceAutopayResult =
  /** The backend ran the charge and reports its outcome — any outcome, including "not charged". */
  | { kind: 'outcome'; result: AdminInvoiceAutopayChargeResult }
  /** A JSend error or a network failure: show `message` verbatim. */
  | { kind: 'error'; message: string }
  /** HTTP 404: the backend does not serve the route. */
  | { kind: 'unavailable' };

/**
 * `POST /admin/invoice-autopay/{clientId}/invoices/{invoiceId}/charge` — charges ONE invoice on the
 * organization's consent. No body: the path carries the client and the backend reads the amount from
 * Facturier itself. A resubmission is harmless (the backend answers `refused` / `busy`).
 */
export async function chargeInvoiceAutopay(
  apiFetch: ApiFetchFn,
  clientId: number,
  invoiceId: number,
): Promise<ChargeInvoiceAutopayResult> {
  try {
    const response = await apiFetch(`/admin/invoice-autopay/${clientId}/invoices/${invoiceId}/charge`, {
      method: 'POST',
    });
    if (response.status === 404) {
      return { kind: 'unavailable' };
    }
    const body = (await response.json().catch(() => null)) as ApiResponseAdminInvoiceAutopayCharge | null;
    if (body?.status === 'success' && body.data?.outcome) {
      return { kind: 'outcome', result: body.data };
    }
    return { kind: 'error', message: body?.message ?? 'The charge could not be made. Please try again.' };
  } catch (err) {
    return { kind: 'error', message: err instanceof Error ? err.message : 'An unexpected network error occurred.' };
  }
}

export default useAdminInvoiceAutopay;
